import { useState, useEffect, useRef } from "react";
import { CustomerType, CustomerConfig, CustomerQuotas } from "../clients/cds_client/api";
import { useRouter } from "next/router";
import { getUser } from "../services/userService";
import { useAuth } from "../contexts/AuthContext";
import { useLayout } from "../contexts/LayoutContext";
import { useScreenType } from "../hooks/useScreenType";
import {
  clearPermissionsCache,
  getPermissionsCacheKey,
  getPermissionsPromiseCache,
  setPermissionsPromiseCache,
} from "./permissionsCache";
import { clearCurrentUserCache } from "./currentUserCache";
import { buildAccessDeniedLoadErrorQuery } from "./errorMessages";
import { setAnalyticsCustomerType } from "../lib/analytics";


export const publicRoutes = [
  "/",
  "/login",
  "/logout",
  "/access-denied",
  "/callback",
  "/auth-callback",
  "/404",
  "/500",
  "/signup",
  "/post-signUp-mobile",
  "/balance-confirmations/counterparty",
  "/start-free"
];

export type FeatureKey =
  | "ai_assistant"
  | "ai_analyst"
  | "pg_recon"
  | "bank_recon"
  | "ledger_recon"
  | "debug"
  | "balance_confirmation"
  | "admin_portal"
  | "master_data"
  | "reminder_settings"
  | "control_center";

// Keep recon types in ONE place to avoid duplicating string unions across helpers.
const RECON_TYPES = ["pg_recon", "bank_recon", "ledger_recon"] as const;
type ReconType = (typeof RECON_TYPES)[number];
const RECON_TYPE_SET: ReadonlySet<string> = new Set(RECON_TYPES);
const isReconType = (value: string | undefined): value is ReconType =>
  value !== undefined && RECON_TYPE_SET.has(value);

const routeToFeature: Record<string, FeatureKey> = {
  "/mobile-home": "ai_assistant",
  "/ai-analyst": "ai_analyst",
  "/ledger-recon-dashboard": "ledger_recon",
  "/recon?type=pg_recon": "pg_recon",
  "/recon?type=bank_recon": "bank_recon",
  "/recon?type=ledger_recon": "ledger_recon",
  "/recon-playground": "debug",
  "/master-datasheet": "master_data",
  "/balance-confirmations": "balance_confirmation",
  "/control-center": "control_center",
  "/control-center/reminder-settings": "reminder_settings",
  "/admin-portal": "admin_portal",
  "/admin-portal/review-portal": "admin_portal",
  "/admin-portal/beacon": "admin_portal",
};

const featurePriority: FeatureKey[] = [
  "ledger_recon", "pg_recon", "bank_recon", "master_data", "balance_confirmation", "reminder_settings", "ai_analyst", "admin_portal" , "debug", "ai_assistant"
];

const permissionHierarchy: Record<FeatureKey, FeatureKey[]> = {
  ledger_recon: ["ledger_recon"],
  pg_recon: ["pg_recon"],
  bank_recon: ["bank_recon"],
  debug: ["debug"],
  ai_analyst: ["ai_analyst"],
  ai_assistant: ["ai_assistant"],
  admin_portal: ["admin_portal"],
  balance_confirmation: ["balance_confirmation"],
  master_data: ["master_data"],
  reminder_settings: ["reminder_settings"],
  control_center: ["control_center"],
};

interface UserPermissions {
  features: Record<FeatureKey, boolean>;
  userName?: string;
  isAdmin?: boolean;
  customerType?: CustomerType;
  quotas?: CustomerQuotas;
}

// Keep only the necessary constants (cache key and promise ref live in permissionsCache.ts)
const CACHE_KEY = getPermissionsCacheKey();

type InitialPermissionsState = {
  permissions: UserPermissions | null;
  isLoading: boolean;
  error: string | null;
  isNoPermissionsError: boolean;
};

const isBrowser = () => typeof window !== "undefined";

const safeSessionGetItem = (key: string): string | null => {
  if (!isBrowser()) return null;
  try {
    return sessionStorage.getItem(key);
  } catch (err) {
    console.warn("Failed to access session storage:", err);
    return null;
  }
};

const safeSessionSetItem = (key: string, value: string): void => {
  if (!isBrowser()) return;
  try {
    sessionStorage.setItem(key, value);
  } catch (err) {
    // Preserve existing behavior: errors here should not crash the app flow.
    throw err;
  }
};

const safeSessionRemoveItem = (key: string): void => {
  if (!isBrowser()) return;
  try {
    sessionStorage.removeItem(key);
  } catch (err) {
    console.warn("Failed to remove from session storage:", err);
  }
};

const readInitialPermissionsStateFromCache = (): InitialPermissionsState => {
  if (!isBrowser()) {
    return { permissions: null, isLoading: true, error: null, isNoPermissionsError: false };
  }

  const cached = safeSessionGetItem(CACHE_KEY);
  if (!cached) {
    return { permissions: null, isLoading: true, error: null, isNoPermissionsError: false };
  }

  try {
    const data = JSON.parse(cached);
    if (data.isNoPermissionsError) {
      return {
        permissions: null,
        isLoading: false,
        error: data.errorMessage,
        isNoPermissionsError: true,
      };
    }
    return {
      permissions: data.permissions,
      isLoading: false,
      error: null,
      isNoPermissionsError: false,
    };
  } catch (err) {
    console.warn("Failed to initialize from session storage:", err);
    return { permissions: null, isLoading: true, error: null, isNoPermissionsError: false };
  }
};

const hasAnyTruePermission = (userPermissions: Record<string, any>) =>
  Object.values(userPermissions).some((p) => p === true);

const createEmptyFeatures = (): Record<FeatureKey, boolean> => ({
  ai_analyst: false,
  pg_recon: false,
  bank_recon: false,
  ledger_recon: false,
  debug: false,
  ai_assistant: false,
  balance_confirmation: false,
  admin_portal: false,
  master_data: false,
  reminder_settings: false,
  control_center: false,
});

const applyDirectPermissions = (
  features: Record<FeatureKey, boolean>,
  userPermissions: Record<string, any>
) => {
  Object.entries(userPermissions).forEach(([key, value]) => {
    if (key in features && value === true) {
      features[key as FeatureKey] = true;
    }
  });
};

const applyHierarchyPermissions = (
  features: Record<FeatureKey, boolean>,
  userPermissions: Record<string, any>
) => {
  Object.entries(userPermissions).forEach(([key, value]) => {
    if (value === true && key in permissionHierarchy) {
      permissionHierarchy[key as FeatureKey].forEach((feature) => {
        // Parent permission grants child permission, override any explicit false values
        features[feature] = true;
      });
    }
  });
};

const applyAdminOverrides = (features: Record<FeatureKey, boolean>, admin: any) => {
  // If admin is true, grant debug permission
  if (admin === "true") {
    features.debug = true;
    features.reminder_settings = true;
    features.control_center = true;
  }
};

const cacheNoPermissionsState = (errorMsg: string) => {
  try {
    safeSessionSetItem(
      CACHE_KEY,
      JSON.stringify({
        isNoPermissionsError: true,
        errorMessage: errorMsg,
        timestamp: Date.now(),
      })
    );
  } catch (err) {
    console.warn("Failed to cache no-permissions state:", err);
  }
};

const cachePermissionsState = (permissions: UserPermissions) => {
  safeSessionSetItem(
    CACHE_KEY,
    JSON.stringify({
      permissions,
    })
  );
};

const getFirstQueryParam = (
  queryParams: Record<string, string | string[] | undefined> | undefined,
  key: string
): string | undefined => {
  const raw = queryParams?.[key];
  return Array.isArray(raw) ? raw[0] : raw;
};

// Helper to build query string from query params
const buildQueryString = (queryParams: Record<string, string | string[] | undefined>): string => {
  return Object.entries(queryParams)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      const paramValue = Array.isArray(value) ? value[0] : value;
      return paramValue !== undefined && paramValue !== null ? `${key}=${encodeURIComponent(paramValue)}` : null;
    })
    .filter(Boolean)
    .join("&");
};

const getReconType = (
  route: string,
  queryParams?: Record<string, string | string[] | undefined>
): ReconType | undefined => {
  if (route !== "/recon" && !route.startsWith("/recon/")) return undefined;
  const type = getFirstQueryParam(queryParams, "type");
  if (isReconType(type)) return type;
  return undefined;
};

const getFeatureForRoute = (
  route: string,
  queryParams?: Record<string, string | string[] | undefined>
): FeatureKey | undefined => {
  // Try to match full route with query parameters first
  if (queryParams && Object.keys(queryParams).length > 0) {
    const queryString = buildQueryString(queryParams);
    if (queryString) {
      const feature = routeToFeature[`${route}?${queryString}`];
      if (feature) return feature;
    }
  }

  // Fall back to checking just the pathname
  const exactFeature = routeToFeature[route];
  if (exactFeature) return exactFeature;

  // Check for parent routes (e.g., /recon/something matches /recon)
  for (const [parentRoute, feature] of Object.entries(routeToFeature)) {
    const parentPathname = parentRoute.split("?")[0];
    if (route.startsWith(parentPathname + "/")) {
      return feature;
    }
  }

  return undefined;
};

const getRouteForFeature = (feature: FeatureKey): string | undefined =>
  Object.keys(routeToFeature).find((r) => routeToFeature[r] === feature);

const isNavigationReload = (): boolean => {
  if (!isBrowser()) return false;
  try {
    const navEntries = performance.getEntriesByType("navigation");
    if (navEntries && navEntries.length > 0) {
      // @ts-ignore
      return navEntries[0].type === "reload";
    }
  } catch (e) {
    console.warn("Navigation performance API not supported:", e);
  }
  return false;
};

// Standalone utility function to refresh permissions
export const refreshPermissions = async (): Promise<void> => {
  try {
    clearPermissionsCache();
    clearCurrentUserCache();

    const userResponse = await getUser();

    if (!userResponse?.data) {
      throw new Error("Unable to load user data");
    }

    return Promise.resolve();
  } catch (error) {
    console.error("Error refreshing permissions:", error);

    // Pass the original error for formatting in access-denied page
    const errorMsg = error instanceof Error ? error.message : "Failed to refresh permissions";

    return Promise.reject(new Error(errorMsg));
  }
};

export const usePermissions = () => {
  const initialState = readInitialPermissionsStateFromCache();

  const [permissions, setPermissions] = useState<UserPermissions | null>(initialState.permissions);
  const [isLoading, setIsLoading] = useState(initialState.isLoading);
  const [error, setError] = useState<string | null>(initialState.error);
  const [isNoPermissionsError, setIsNoPermissionsError] = useState(initialState.isNoPermissionsError);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { setCustomerName, setUserName } = useLayout();
  const screenType = useScreenType();
  const initialLoadRef = useRef(!initialState.isLoading);
  const validatingRef = useRef(false);

  const hasCache = () => !!safeSessionGetItem(CACHE_KEY);

  const loadPermissions = () => {
    setIsLoading(true);
    setError(null);
    setIsRefreshing(true);

    const existing = getPermissionsPromiseCache();
    if (!existing) {
      // Only the first bootstrap should clear caches; multiple `usePermissions()` instances
      // otherwise each clearCurrentUserCache() invalidates getUser() dedupe and duplicates CDS calls.
      if (isNavigationReload()) {
        clearPermissionsCache();
        clearCurrentUserCache();
      }
      const promise = getUser()
        .then((userResponse) => {
          if (!userResponse?.data) throw new Error("Unable to load user data");
          return userResponse;
        })
        .catch((err) => {
          setPermissionsPromiseCache(null);
          throw err;
        });
      setPermissionsPromiseCache(promise);
    }
    const permissionsPromiseCache = getPermissionsPromiseCache();
    return permissionsPromiseCache!
      .then((userResponse) => {
        const userData = userResponse.data;
        const userPermissions = userData.permission;
        const admin = userData.admin;
        const hasAnyPermissions = hasAnyTruePermission(userPermissions);

        if (!hasAnyPermissions) {
          const errorMsg = "You don't have any permissions assigned. Please contact your administrator.";
          setIsNoPermissionsError(true);
          setError(errorMsg);
          setPermissions(null);
          cacheNoPermissionsState(errorMsg);

          setIsLoading(false);
          setIsRefreshing(false);
          return;
        }

        const features = createEmptyFeatures();
        applyDirectPermissions(features, userPermissions);
        applyHierarchyPermissions(features, userPermissions);
        applyAdminOverrides(features, admin);

        const permissionsData: UserPermissions = {
          features,
          userName: `${userData.first_name || ""} ${userData.last_name || ""}`.trim(),
          isAdmin: admin === "true",
          customerType: userData.customer_type,
          quotas: userData.config?.quotas ?? undefined,
        };

        // Keep LayoutContext in sync without extra API calls.
        // This runs off the same cached getUser() request that powers permissions.
        setUserName(permissionsData.userName ?? "");
        setCustomerName(userData.customer_name ?? "");

        setPermissions(permissionsData);
        setError(null);
        setIsNoPermissionsError(false);
        cachePermissionsState(permissionsData);
        setIsLoading(false);
        setIsRefreshing(false);
      })
      .catch((err) => {
        // Pass the original error to access-denied page for formatting
        const errorMsg = err instanceof Error ? err.message : "Failed to load permissions";

        // Drop stale no-permissions cache so a load failure is not shown as zero permissions.
        clearPermissionsCache();

        setError(errorMsg);
        setPermissions(null);
        setIsNoPermissionsError(false);
        setIsLoading(false);
        setIsRefreshing(false);
        setPermissionsPromiseCache(null);
      })
      .finally(() => {
        // Mark that we have completed at least one permissions load attempt
        // (even if it resulted in an error / no-permissions state).
        initialLoadRef.current = true;
      });
  };

  const hasPermission = (feature: FeatureKey): boolean => {
    return permissions?.features[feature] || false;
  };

  const isPublicRoute = (route: string): boolean => {
    return publicRoutes.includes(route);
  };

  const hasRoutePermission = (route: string, queryParams?: Record<string, string | string[] | undefined>): boolean => {

    if (route === "/mobile-home") {
      return screenType === "mobile";
    }

    const reconType = getReconType(route, queryParams);
    if (reconType) return hasPermission(reconType as FeatureKey);

    const feature = getFeatureForRoute(route, queryParams);
    return feature ? hasPermission(feature) : false;
  };

  // Updated findFirstAccessibleRoute with mobile and TDS setup check
  const findFirstAccessibleRoute = (): Promise<string> => {
    if (!permissions || isNoPermissionsError) return Promise.resolve("/access-denied");

    // Existing logic for non-mobile or fallback routes
    for (const feature of featurePriority) {
      if (hasPermission(feature)) {
        const route = getRouteForFeature(feature);
        if (route) {
          if (route === "/mobile-home" && screenType !== "mobile") continue;


          return Promise.resolve(route);
        }
      }
    }
    return Promise.resolve("/access-denied");
  };

  // Keep validateRoute as non-async
  const validateRoute = () => {
    // Wait for router to be ready before validating to ensure query params are available
    if (validatingRef.current || !initialLoadRef.current || !screenType || isLoading || !router.isReady) return;

    validatingRef.current = true;
    const currentRoute = router.pathname;

    try {
      if (currentRoute === "/access-denied") {
        if (!isNoPermissionsError && !error && permissions) {
          findFirstAccessibleRoute().then((redirectUrl) => {
            if (redirectUrl !== "/access-denied") {
              router.replace(redirectUrl);
            }
          });
        }
        return;
      }

      if (isPublicRoute(currentRoute)) return;

      if (isNoPermissionsError) {
        router.replace({
          pathname: "/access-denied",
          query: {
            isPermissionError: "true",
            errorMessage:
              error ||
              "You don't have any permissions assigned to your account. Please contact your administrator.",
            isNoPermissionsError: "true",
          },
        });
        return;
      }

      if (error) {
        router.replace({
          pathname: "/access-denied",
          query: buildAccessDeniedLoadErrorQuery(error),
        });
        return;
      }

      // Regular route permission check
      findFirstAccessibleRoute().then((accessibleRoute) => {
        if (!hasRoutePermission(currentRoute, router.query)) {
          // Get feature for error message
          const feature = getFeatureForRoute(currentRoute, router.query);

          if (accessibleRoute !== "/access-denied") {
            router.replace(accessibleRoute);
            return;
          }

          let errorMessage = "Access denied";
          if (currentRoute === "/mobile-home" && screenType !== "mobile") {
            errorMessage = "This page is only available on mobile devices";
          } else if (feature) {
            errorMessage = `No permission for ${feature} feature`;
          }

          router.replace({
            pathname: "/access-denied",
            query: {
              errorMessage,
              redirectUrl: accessibleRoute
            }
          });
        }
      });
    } finally {
      validatingRef.current = false;
    }
  };

  // ... (keep existing useEffect hooks unchanged)
  useEffect(() => {
    const reload = isNavigationReload();
    if (!isAuthenticated) {
      // Clear permissions on logout so they don't persist for the next user/session
      clearPermissionsCache();
      clearCurrentUserCache();
      setPermissions(null);
      setError(null);
      setIsNoPermissionsError(false);
      setIsLoading(false);
      setIsRefreshing(false);
      return;
    }
    if (reload) {
      loadPermissions();
    } else if (!hasCache()) {
      loadPermissions();
    }

    // No cleanup that nulls `permissionsPromiseCache`: doing so on unmount interacted badly
    // with React 18 Strict Mode (remount → duplicate getCurrentUser). Logout clears via
    // `clearPermissionsCache()` in the `!isAuthenticated` branch above.
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAnalyticsCustomerType(null);
      return;
    }
    if (permissions?.customerType) {
      setAnalyticsCustomerType(permissions.customerType);
    }
  }, [isAuthenticated, permissions?.customerType]);

  useEffect(() => {
    validateRoute();
  }, [router.pathname, router.isReady, router.query, permissions, error, isNoPermissionsError, screenType, isLoading]);

  return {
    permissions,
    isLoading,
    error,
    hasPermission,
    hasRoutePermission,
    findFirstAccessibleRoute,
    userName: permissions?.userName || "",
    isPublicRoute,
    isNoPermissionsError,
    screenType,
    isRefreshing,
    isAdmin: permissions?.isAdmin || false,
    customerType: permissions?.customerType ?? CustomerType.Enterprise,
    quotas: permissions?.quotas ?? {},
  };
};